import { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Empty, List, Modal, Tag, Typography } from 'antd';

import UserRef from '../users/UserRef';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { fetchExportRunFile, getExportRuns } from '../../services/exportDefinitions';
import { downloadBlob } from '../../services/exports';
import {
	DELIVERY_STATUS_LABELS,
	RUN_STATUS_COLORS,
	RUN_STATUS_LABELS,
	SKIP_REASON_WORDS,
	SUPPRESSION_WORDS,
	formatBytes,
} from '../../utils/exportDefinitions';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import { joinMeta } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { slugify } from '../../utils/strings';
import { getUserId, isAdmin } from '../../utils';
import useDateFormat from '../../hooks/useDateFormat';
import { DOWNLOAD } from '../icons';
import { anchorOf } from '../../platform';

const { Text } = Typography;

/**
 * One stored export's run ledger: did it go out, to whom, and what was in it.
 *
 * Three things are visible here that are visible nowhere else, and each is a
 * way a scheduled export can be quietly wrong:
 *
 *   * TRUNCATION. A delivered file carries no headers, so "1,000 of 7,231"
 *     lives on the run.
 *   * A SKIP — the owner lost access or is unverified, the collection was
 *     archived — said as plainly as a success.
 *   * A DELIVERY THAT DID NOT HAPPEN, per recipient, with its reason.
 *
 * The file itself downloads for the run's OWNER (or an admin) alone: it was
 * rendered through that person's access. Anyone else is told whose it is.
 * After the retention window keepr deletes it, and the run says when.
 *
 * Rows stack rather than tabulate, so the ledger reads on a phone without
 * scrolling sideways. Paging is Previous / Next: the API returns a page, not
 * a count.
 */

export const RUNS_PAGE_SIZE = 20;

const currentUserId = () => {
	const id = getUserId();
	return id && id !== 'undefined' ? id : null;
};

const n = value => Number( value || 0 ).toLocaleString();

function DeliveryLines( { deliveries } ) {
	const rows = Array.isArray( deliveries ) ? deliveries : [];
	if ( ! rows.length ) return <Text type='secondary' className='export-run__line'>Nobody was emailed.</Text>;
	return (
		<ul className='export-run__deliveries'>
			{rows.map( ( delivery, index ) => {
				const reason = delivery.suppressed || delivery.skipped;
				return (
					<li key={`${ delivery.to || delivery.recipient?.userId || 'recipient' }-${ index }`}>
						<Tag color={delivery.status === 'sent' ? 'green' : delivery.status === 'failed' ? 'red' : 'default'}>
							{DELIVERY_STATUS_LABELS[ delivery.status ] || delivery.status || 'Unknown'}
						</Tag>
						<span className='export-run__recipient'>
							{delivery.recipient?.type === 'user' && delivery.recipient?.userId
								? <UserRef userId={delivery.recipient.userId} size={18} />
								: ( delivery.to || 'An account with no address' )}
						</span>
						{reason ? (
							<Text type='secondary'>{` — ${ SUPPRESSION_WORDS[ reason ] || reason }`}</Text>
						) : null}
						{delivery.error ? <Text type='secondary'>{` — ${ delivery.error }`}</Text> : null}
					</li>
				);
			} )}
		</ul>
	);
}

function FileLine( { run, mayDownload, downloading, onDownload } ) {
	if ( run.fileReapedAt ) {
		return (
			<Text type='secondary' className='export-run__line'>
				{`File removed after the retention period, ${ formatDisplayDate( run.fileReapedAt ) }.`}
			</Text>
		);
	}
	if ( ! run.file_id ) return null;
	const kept = run.fileExpiresAt ? `Kept until ${ formatDisplayDate( run.fileExpiresAt ) }.` : null;
	if ( ! mayDownload ) {
		return (
			<Text type='secondary' className='export-run__line'>
				{joinMeta( kept, 'Only the person it ran as can download it here; recipients have their own links.' )}
			</Text>
		);
	}
	return (
		<div className='export-run__file'>
			<Button size='small' icon={<DOWNLOAD />} loading={downloading} onClick={onDownload}>
				Download
			</Button>
			{kept ? <Text type='secondary'>{kept}</Text> : null}
		</div>
	);
}

export default function ExportRunsModal( { open, collectionId, definition, page = 1, onPage, onClose } ) {

	const { message } = App.useApp();
	const [ rows, setRows ] = useState( [] );
	const [ loading, setLoading ] = useState( false );
	const [ loadError, setLoadError ] = useState( false );
	// A read has answered for this export; a page change then keeps the old page under the refresh cue.
	const [ loaded, setLoaded ] = useState( false );
	const [ attempt, setAttempt ] = useState( 0 );
	const loadedFor = useRef( null );
	const [ downloading, setDownloading ] = useState( null );
	const exportId = normalizeId( definition?._id );
	const me = currentUserId();
	const admin = isAdmin();

	useDateFormat();

	useEffect( () => {
		if ( ! open || ! collectionId || ! exportId ) return undefined;
		let active = true;
		if ( loadedFor.current !== exportId ) {
			loadedFor.current = exportId;
			setRows( [] );
			setLoaded( false );
		}
		setLoading( true );
		setLoadError( false );
		getExportRuns( collectionId, exportId, { limit: RUNS_PAGE_SIZE, skip: ( page - 1 ) * RUNS_PAGE_SIZE } )
			.then( data => { if ( active ) { setRows( data ); setLoaded( true ); } } )
			.catch( () => { if ( active ) setLoadError( true ); } )
			.finally( () => { if ( active ) setLoading( false ); } );
		return () => { active = false; };
	}, [ open, collectionId, exportId, page, attempt ] );

	const handleDownload = useCallback( async (run, event) => {
		const anchor = anchorOf( event );
		const runId = normalizeId( run?._id );
		setDownloading( runId );
		try {
			const blob = await fetchExportRunFile( collectionId, exportId, runId );
			const stamp = run?.startedAt ? `-${ `${ run.startedAt }`.slice( 0, 10 ) }` : '';
			await downloadBlob( blob, `${ slugify( definition?.name ) || 'export' }${ stamp }.${ definition?.format || 'pdf' }`, { anchor } );
		}
		catch ( err ) {
			const status = err?.response?.status;
			message.error( status === 404
				? 'That file has been removed: exports are kept only for their retention period.'
				: status === 403
					? 'Only the person this export ran as can download its file.'
					: 'Unable to download this file.' );
		}
		finally {
			setDownloading( null );
		}
	}, [ collectionId, exportId, definition, message ] );

	const renderRun = run => {
		const runId = normalizeId( run?._id );
		const status = run?.status || 'unknown';
		const mayDownload = admin || ( me && normalizeId( run?.ownerId ) === me );
		const facts = joinMeta(
			run?.truncated
				? `${ n( run.rows ) } of ${ n( run.total ) } rows`
				: ( run?.status === 'succeeded' ? `${ n( run.rows ) } ${ Number( run.rows ) === 1 ? 'row' : 'rows' }` : null ),
			formatBytes( run?.bytes ),
		);
		return (
			<List.Item key={runId} className='export-run'>
				<div className='export-run__body'>
					<div className='export-run__head'>
						<Tag color={RUN_STATUS_COLORS[ status ] || 'default'}>{RUN_STATUS_LABELS[ status ] || status}</Tag>
						{run?.truncated ? <Tag color='gold'>Cut short</Tag> : null}
						<Text>{formatDisplayDateTime( run?.startedAt )}</Text>
						<Text type='secondary'>
							{run?.trigger?.type === 'schedule' ? 'Scheduled' : 'Run now'}
						</Text>
					</div>
					{run?.ownerId ? (
						<div className='export-run__line export-run__owner'>
							<Text type='secondary'>Ran as</Text>
							<UserRef userId={run.ownerId} size={18} />
						</div>
					) : null}
					{facts ? <Text type='secondary' className='export-run__line'>{facts}</Text> : null}
					{run?.truncated ? (
						<Text className='export-run__line'>
							{`Cut short at the limit: ${ n( Number( run.total ) - Number( run.rows ) ) } more matched. Narrow what it exports to include them.`}
						</Text>
					) : null}
					{run?.skipped ? (
						<Text className='export-run__line'>
							{`Nothing was exported: ${ SKIP_REASON_WORDS[ run.skipped ] || run.skipped }.`}
						</Text>
					) : null}
					{run?.error ? <Text type='danger' className='export-run__line'>{run.error}</Text> : null}
					<DeliveryLines deliveries={run?.deliveries} />
					<FileLine
						run={run}
						mayDownload={mayDownload}
						downloading={downloading === runId}
						onDownload={event => handleDownload( run, event )}
					/>
				</div>
			</List.Item>
		);
	};

	const title = definition?.name ? `Runs of ${ definition.name }` : 'Runs';

	return (
		<Modal
			open={open}
			onCancel={onClose}
			width={720}
			centered
			destroyOnClose
			className='export-runs'
			title={title}
			footer={[
				<Button key='prev' disabled={page <= 1 || loading} onClick={() => onPage?.( page - 1 )}>Previous</Button>,
				<Button key='next' disabled={rows.length < RUNS_PAGE_SIZE || loading} onClick={() => onPage?.( page + 1 )}>Next</Button>,
				<Button key='close' onClick={onClose}>Close</Button>,
			]}
		>
			<DialogLoadState
				loaded={loaded}
				loading={loading}
				error={loadError}
				label='the runs of this export'
				onRetry={() => setAttempt( n => n + 1 )}
				skeleton={<Skeleton variant='list-row' count={4} />}
				minHeight={280}
			>
				<List
					dataSource={rows}
					rowKey={run => normalizeId( run?._id )}
					renderItem={renderRun}
					locale={{ emptyText: (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description={page > 1 ? 'No earlier runs.' : 'No runs yet. It runs on its schedule, or when its owner chooses Run now.'}
						/>
					) }}
				/>
			</DialogLoadState>
		</Modal>
	);
}
